/**
 * "Help a Job Seeker" — a light nudge to share, shown after a visitor has
 * already gotten value (exported their resume, or read a guide to its
 * end-of-article CTA). Never on page load; never more than once per 30-day
 * cooldown, shared across both triggers — see `lib/sharePrompt.ts`.
 *
 * "Share Now" hands off to the existing `ShareModal` with the site's existing
 * default share resource — this component owns none of the actual sharing
 * logic (platform URLs, copy-to-clipboard, native share sheet), only the ask.
 */

import { useEffect, useRef } from 'react'
import { Modal } from '@/components/ui/Modal'
import { useSharePromptStore } from '@/store/sharePromptStore'
import { useShareStore } from '@/store/shareStore'
import { HOMEPAGE_SHARE_RESOURCE } from '@/lib/share'
import { track } from '@/lib/analytics'

export const SharePromptModal = () => {
  const open = useSharePromptStore((s) => s.open)
  const context = useSharePromptStore((s) => s.context)
  const closeSharePrompt = useSharePromptStore((s) => s.closeSharePrompt)
  const openShare = useShareStore((s) => s.openShare)

  // `Modal`'s onClose can fire twice for one dismissal — once from a direct
  // click (the header X, or this backdrop/Escape path), once more from the
  // native <dialog> `close` event its own effect triggers as a side effect of
  // `open` flipping to false. This ref makes the response (and its analytics
  // event) fire exactly once per time the prompt is shown, however it closes.
  const respondedRef = useRef(false)

  useEffect(() => {
    if (open) {
      respondedRef.current = false
      track('share_prompt_shown')
    }
  }, [open])

  const handleMaybeLater = () => {
    if (!respondedRef.current) {
      respondedRef.current = true
      track('share_prompt_dismissed')
    }
    closeSharePrompt()
  }

  const handleShareNow = () => {
    respondedRef.current = true
    track('share_prompt_clicked')
    closeSharePrompt()
    openShare(HOMEPAGE_SHARE_RESOURCE, `share-prompt:${context}`)
  }

  return (
    <Modal
      open={open}
      onClose={handleMaybeLater}
      title="🚀 Help a Job Seeker"
      labelledBy="share-prompt-title"
      footer={
        <>
          <button type="button" onClick={handleMaybeLater} className="btn btn-ghost min-h-11">
            Maybe Later
          </button>
          <button type="button" onClick={handleShareNow} className="btn btn-primary min-h-11">
            Share Now
          </button>
        </>
      }
    >
      <p className="text-sm">
        Found this useful? Share ATSResumeKit on LinkedIn or social media. Your one share could help
        someone create a better resume and get closer to their next job.
      </p>
      <p className="mt-3 text-sm opacity-70">❤️ Thanks for helping us reach more job seekers!</p>
    </Modal>
  )
}
